import Autocomplete from '@mui/material/Autocomplete';
import React from 'react';
import * as Yup from 'yup';
import type { ResetType } from '../../../../app/ApiGen';
import { AutocompleteContents, renderAutocompleteInput } from '../../../../components/CustomFields/SimpleAutocomplete';
import { WizardStep } from '../../../../components/Wizard/WizardStep';
import { InputComponentProps, OnSubmit, useWizard } from '../../../../design-system';
import { createSchema, FormFields } from '../../../../utils/formUtils';
import { CommandsWizardState, CommandType, ResetStepFields } from '../types';

const resetTypeOptions: ResetType[] = ['Soft', 'Hard'];

const fields: FormFields<ResetStepFields> = {
  resetType: {
    id: 'resetType',
    label: 'Reset type',
    inputComponent: ({
      disabled,
      error,
      helperText,
      name,
      onChange,
      value: selectedResetType,
    }: InputComponentProps<ResetType>) => (
      <Autocomplete<ResetType, false, true>
        disableClearable
        fullWidth
        getOptionLabel={(option) => option ?? ''}
        handleHomeEndKeys
        onChange={(event, command) => {
          onChange(name)({ ...event, target: { ...event.target, value: command } });
        }}
        options={resetTypeOptions}
        renderInput={renderAutocompleteInput(disabled, error, helperText)}
        value={selectedResetType}
      />
    ),
    valueComponent: ({ value }: AutocompleteContents<ResetType>) => <>{value}</>,
  },
};

const validationSchema = createSchema<ResetStepFields>({
  resetType: Yup.mixed<ResetType>().required('Reset type is required'),
});

export const ResetStep: React.FC = () => {
  const { state: untypedState, setState: untypedSetState, prevStep, nextStep, step } = useWizard();
  const state = untypedState as Partial<CommandsWizardState>;
  const setState = untypedSetState as (state: Partial<CommandsWizardState>) => void;

  const savedReset = state.command?.type === CommandType.RESET ? state.command : undefined;

  const onCancel = React.useCallback<OnSubmit<ResetStepFields>>(
    (values) => {
      setState({ command: { type: CommandType.RESET, resetType: values.resetType } });
      prevStep();
    },
    [setState, prevStep],
  );

  const onSubmit = React.useCallback<OnSubmit<ResetStepFields>>(
    (values, { setFieldError }) => {
      setFieldError('resetType', undefined);
      setState({ command: { type: CommandType.RESET, resetType: values.resetType } });
      nextStep();
    },
    [setState, nextStep],
  );

  return (
    <WizardStep<ResetStepFields>
      fields={Object.values(fields)}
      initialValues={{ resetType: savedReset?.resetType ?? 'Soft' }}
      name="Enter reset type"
      onCancel={onCancel}
      onSubmit={onSubmit}
      step={step}
      subtitle="Clicking 'Next' will start resetting the charge points."
      validationSchema={validationSchema}
    />
  );
};
